import { Box } from '@mui/material'
import { FC } from 'react'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import type { ShiftCellAssignment } from './ShiftScheduleGrid'
import {
	SHIFT_CARD_GAP,
	SHIFT_CARD_HEIGHT,
	SHIFT_CELL_ADD_PLATE_HEIGHT,
} from './shiftScheduleLayout'

interface ShiftCellProps {
	slotCount?: number
	isPastDate?: boolean
	assignments?: ShiftCellAssignment[]
	selected?: boolean
	onAssignmentClick?: (assignment: ShiftCellAssignment) => void
	onAddClick?: () => void
}

const PAST_SHIFT_TEXT_COLOR = '#8D8D96'
const ACTIVE_SHIFT_TITLE_COLOR = '#8D8D96'
const ACTIVE_SHIFT_TIME_COLOR = '#1A1A1A'

const placeholderSx = {
	height: `${SHIFT_CARD_HEIGHT}px`,
	flexShrink: 0,
	borderRadius: '8px',
	backgroundColor: '#FFFFFF91',
}

const plusSx = {
	color: '#5222D0',
	fontSize: '24px',
	lineHeight: 1,
	userSelect: 'none' as const,
	opacity: 0,
	transition: 'opacity 0.15s ease',
}

const interactivePlaceholderSx = {
	...placeholderSx,
	width: '100%',
	border: 'none',
	padding: 0,
	cursor: 'pointer',
	transition: 'background-color 0.2s ease',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	'&:hover': {
		backgroundColor: '#5222D00D',
		'& .shift-cell-plus': {
			opacity: 1,
		},
	},
	'&:active': {
		transform: 'scale(0.98)',
	},
}

export const ShiftCell: FC<ShiftCellProps> = ({
	slotCount = 1,
	isPastDate = false,
	assignments = [],
	selected = false,
	onAssignmentClick,
	onAddClick,
}) => {
	const placeholderCount = Math.max(slotCount - assignments.length, 0)
	const canAddShift = !isPastDate
	const cellSx = {
		width: '100%',
		minWidth: 0,
		maxWidth: '100%',
		position: 'relative' as const,
		alignSelf: 'flex-start' as const,
	}

	if (assignments.length === 0) {
		if (!canAddShift) {
			return (
				<Box
					sx={{
						...cellSx,
						display: 'flex',
						flexDirection: 'column',
						gap: `${SHIFT_CARD_GAP}px`,
					}}
				>
					{Array.from({ length: slotCount }).map((_, index) => (
						<Box key={`empty-slot-${index}`} sx={placeholderSx} />
					))}
				</Box>
			)
		}

		return (
			<Box
				sx={{
					...cellSx,
					display: 'flex',
					flexDirection: 'column',
					gap: `${SHIFT_CARD_GAP}px`,
				}}
			>
				{Array.from({ length: slotCount }).map((_, index) => (
					<Box
						key={`empty-slot-${index}`}
						component='button'
						type='button'
						onClick={onAddClick}
						sx={interactivePlaceholderSx}
					>
						<TypographyPrimary className='shift-cell-plus' sx={plusSx}>
							+
						</TypographyPrimary>
					</Box>
				))}
			</Box>
		)
	}

	return (
		<Box
			sx={{
				...cellSx,
				display: 'flex',
				flexDirection: 'column',
				gap: `${SHIFT_CARD_GAP}px`,
				overflow: 'visible',
				...(canAddShift
					? {
							'&:hover .shift-cell-add': {
								opacity: 1,
								pointerEvents: 'auto',
							},
						}
					: {}),
			}}
		>
			{assignments.map(assignment => (
				<Box
					key={assignment.id}
					sx={{
						width: '100%',
						flexShrink: 0,
						borderRadius: '12px',
						boxShadow: selected ? '0 0 0 2px #5222D0' : 'none',
					}}
				>
					<Box
						component='button'
						type='button'
						onClick={() => onAssignmentClick?.(assignment)}
						sx={{
							width: '100%',
							height: `${SHIFT_CARD_HEIGHT}px`,
							flexShrink: 0,
							backgroundColor: '#FFFFFF',
							borderRadius: '12px',
							border: 'none',
							outline: 'none',
							boxShadow: '0 2px 8px 0 #12121214',
							borderLeft: `4px solid ${assignment.color}`,
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							gap: '2px',
							textAlign: 'left',
							px: '10px',
							overflow: 'hidden',
							cursor: 'pointer',
							'&:focus-visible': {
								outline: 'none',
							},
						}}
					>
						<TypographyPrimary
							sx={{
								color: isPastDate
									? PAST_SHIFT_TEXT_COLOR
									: ACTIVE_SHIFT_TITLE_COLOR,
								fontSize: '11px',
								lineHeight: 1.2,
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								display: 'block',
								maxWidth: '100%',
							}}
						>
							{assignment.title}
						</TypographyPrimary>
						<TypographyPrimary
							sx={{
								color: isPastDate
									? PAST_SHIFT_TEXT_COLOR
									: ACTIVE_SHIFT_TIME_COLOR,
								fontSize: '15px',
								lineHeight: 1.2,
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								display: 'block',
								maxWidth: '100%',
							}}
						>
							{assignment.timeRange}
						</TypographyPrimary>
						<TypographyPrimary
							sx={{
								color: PAST_SHIFT_TEXT_COLOR,
								fontSize: '11px',
								lineHeight: 1.2,
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								display: 'block',
								maxWidth: '100%',
							}}
						>
							{assignment.parlorName}
						</TypographyPrimary>
					</Box>
				</Box>
			))}
			{canAddShift &&
				Array.from({ length: placeholderCount }).map((_, index) => (
					<Box
						key={`placeholder-${index}`}
						component='button'
						type='button'
						onClick={onAddClick}
						sx={interactivePlaceholderSx}
					>
						<TypographyPrimary className='shift-cell-plus' sx={plusSx}>
							+
						</TypographyPrimary>
					</Box>
				))}
			{canAddShift && placeholderCount === 0 && (
				<Box
					component='button'
					type='button'
					className='shift-cell-add'
					onClick={onAddClick}
					sx={{
						position: 'absolute',
						left: 0,
						right: 0,
						top: '100%',
						height: `${SHIFT_CELL_ADD_PLATE_HEIGHT}px`,
						border: 'none',
						borderRadius: '6px',
						backgroundColor: '#5222D00D',
						cursor: 'pointer',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						opacity: 0,
						pointerEvents: 'none',
						transition: 'opacity 0.15s ease',
						zIndex: 2,
						'&:active': {
							transform: 'scale(0.95)',
						},
					}}
				>
					<TypographyPrimary
						sx={{
							color: '#5222D0',
							fontSize: '16px',
							lineHeight: 1,
							userSelect: 'none',
						}}
					>
						+
					</TypographyPrimary>
				</Box>
			)}
			{!canAddShift &&
				Array.from({ length: placeholderCount }).map((_, index) => (
					<Box key={`placeholder-${index}`} sx={placeholderSx} />
				))}
		</Box>
	)
}
